import { z } from 'zod';

/**
 * Org plans. One catalog, in code; per-org overrides (`orgs.limit_overrides`)
 * cover negotiated deals. Limits apply to a whole org tree and are read from
 * the root org's plan: a university's seats cover all its affiliated colleges.
 *
 * Numbers are placeholders until commercial terms are set.
 */
export const LIMIT_KEYS = [
  'maxMembers',
  'maxChildOrgs',
  'maxAdmins',
  'maxActiveContests',
  'maxDomains',
] as const;
export type LimitKey = (typeof LIMIT_KEYS)[number];
export type Limits = Record<LimitKey, number>;

export const PLAN_IDS = ['pilot', 'campus_partner', 'enterprise'] as const;
export const planIdSchema = z.enum(PLAN_IDS);
export type PlanId = z.infer<typeof planIdSchema>;

export interface Plan {
  id: PlanId;
  name: string;
  description: string;
  limits: Limits;
}

export const PLANS: Record<PlanId, Plan> = {
  pilot: {
    id: 'pilot',
    name: 'Pilot',
    description: 'Try Nirvik Ready with a small group.',
    limits: { maxMembers: 100, maxChildOrgs: 0, maxAdmins: 3, maxActiveContests: 1, maxDomains: 1 },
  },
  campus_partner: {
    id: 'campus_partner',
    name: 'Campus Partner',
    description: 'A university or company with affiliated colleges or teams.',
    limits: {
      maxMembers: 5_000,
      maxChildOrgs: 50,
      maxAdmins: 25,
      maxActiveContests: 10,
      maxDomains: 5,
    },
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Large organizations; limits set per contract.',
    limits: {
      maxMembers: 50_000,
      maxChildOrgs: 500,
      maxAdmins: 200,
      maxActiveContests: 100,
      maxDomains: 25,
    },
  },
};

export const DEFAULT_PLAN: PlanId = 'pilot';

export const limitOverridesSchema = z
  .object(
    Object.fromEntries(
      LIMIT_KEYS.map((k) => [k, z.number().int().min(0).max(10_000_000)]),
    ) as Record<LimitKey, z.ZodNumber>,
  )
  .partial();
export type LimitOverrides = z.infer<typeof limitOverridesSchema>;

/** Plan defaults with any per-org overrides applied. Unknown plan ids fall back to the default plan. */
export function effectiveLimits(planId: string, overrides?: LimitOverrides | null): Limits {
  const plan = PLANS[planId as PlanId] ?? PLANS[DEFAULT_PLAN];
  return { ...plan.limits, ...(overrides ?? {}) };
}

/** Share of a limit used, for 80% / 100% warnings. A limit of 0 counts as full. */
export function usageLevel(used: number, limit: number): 'ok' | 'warning' | 'full' {
  if (limit <= 0 || used >= limit) return 'full';
  return used / limit >= 0.8 ? 'warning' : 'ok';
}
